// apps/client/src/hooks/pipelineTracker/useSnapshotFreshness.ts
//
// How current the published data is, for the page header.
//
// Derived from the SAME upload history the Upload tab lists, so the header can
// never disagree with it. It reports the newest COMMITTED upload: a failed or
// superseded attempt is not freshness, and counting one would tell a tenant
// their figures are current when the upload that would have made them current
// was refused.

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { INGESTION_CALLABLE, LIST_UPLOADS_ACTION } from './reportContract';
import { invalidateTrackerReports } from './reportCache';

export interface SnapshotFreshness {
  asAtDate: string;
  label: string;
  /** Past the weekly cadence the freshness sweep uses. */
  stale: boolean;
}

const STALE_AFTER_DAYS = 7;

/**
 * The committed uploads last seen per tenant, this session. When the page opens and the list has
 * changed (an upload made elsewhere: another person, or the founder support door), every cached
 * report answer predates it and is dropped.
 */
const committedSeen = new Map<string, string>();

const formatAsAt = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
};

export function useSnapshotFreshness(companyId: string | undefined): SnapshotFreshness | null {
  const [freshness, setFreshness] = useState<SnapshotFreshness | null>(null);

  useEffect(() => {
    if (!companyId) return undefined;
    let alive = true;

    (async () => {
      try {
        const call = httpsCallable(functions, INGESTION_CALLABLE);
        const res = await call({ action: LIST_UPLOADS_ACTION, companyId, limit: 20 });
        const uploads = (res.data as { uploads?: Array<{ state?: string; asAtDate?: string; uploadId?: string }> })?.uploads ?? [];

        const committed = JSON.stringify(
          uploads.filter((u) => u.state === 'committed').map((u) => [u.uploadId ?? null, u.asAtDate ?? null]),
        );
        const seen = committedSeen.get(companyId);
        if (alive && seen !== undefined && seen !== committed) invalidateTrackerReports();
        if (alive) committedSeen.set(companyId, committed);

        const newest = uploads
          .filter((u) => u.state === 'committed' && typeof u.asAtDate === 'string')
          .map((u) => u.asAtDate as string)
          .sort()
          .pop();

        if (!alive || !newest) return;

        const ageDays = Math.floor(
          (Date.now() - Date.parse(`${newest}T00:00:00Z`)) / 86_400_000,
        );
        const stale = ageDays > STALE_AFTER_DAYS;
        setFreshness({
          asAtDate: newest,
          stale,
          label: stale
            ? `${ageDays} days since ${formatAsAt(newest)}`
            : `Fresh, as at ${formatAsAt(newest)}`,
        });
      } catch {
        // Freshness is a header ornament. A tenant who cannot read it still has
        // a working page, so this stays silent rather than raising a banner.
        if (alive) setFreshness(null);
      }
    })();

    return () => { alive = false; };
  }, [companyId]);

  return freshness;
}
